import { DFS, Integration } from "@/types/general";

type DataTransferOperations = DFS.Components.DataTransferOperations;

enum DataTransferProgressStatus {
    IDLE = "idle",
    ACTIVE = "active",
    FINISHED = "finished",
}

interface DataTransferStateBase {
    operation: DataTransferOperations;
    status: DataTransferProgressStatus;
    module?: string;
    /**
     * Entities covered by an integration import. Known only to the client that started the import,
     * since the sockets and the `data-transfer/current` endpoint report just the module and operation.
     */
    entities?: Integration.Import.Entity[];
}

interface DataTransferStateIdle extends DataTransferStateBase {
    status: DataTransferProgressStatus.IDLE;
}

interface DataTransferStateActive extends DataTransferStateBase {
    status: DataTransferProgressStatus.ACTIVE;
    progress?: { total: number; current: number }[];
    errors?: number;
}

interface DataTransferStateFinished extends DataTransferStateBase {
    status: DataTransferProgressStatus.FINISHED;
    has_errors: boolean;
    /**
     * Number of records that failed during the transfer, taken from the last progress update.
     * Row-level failures don't fail the transfer itself, so `has_errors` can be false while this is positive.
     */
    errors?: number;
    data?: any;
}

type DataTransferState = DataTransferStateIdle | DataTransferStateActive | DataTransferStateFinished;

function isDataTransferStateActive(state?: DataTransferState): state is DataTransferStateActive {
    return state?.status === DataTransferProgressStatus.ACTIVE;
}

function isDataTransferStateFinished(state?: DataTransferState): state is DataTransferStateFinished {
    return state?.status === DataTransferProgressStatus.FINISHED;
}

/**
 * A utility selector to get the mean progress of all active imports
 * @param state active import object
 */
const selectProgressMean = (state: DataTransferStateActive) => {
    const { progress } = state;

    if (!progress?.length) return 0;

    const totalProgress = progress.reduce((sum, p) => sum + p.current, 0);
    const totalTotal = progress.reduce((sum, p) => sum + p.total, 0);
    return totalTotal === 0 ? 0 : totalProgress / totalTotal;
};

export { isDataTransferStateActive, isDataTransferStateFinished, selectProgressMean, DataTransferProgressStatus };
export type {
    DataTransferState,
    DataTransferStateIdle,
    DataTransferStateActive,
    DataTransferStateFinished,
    DataTransferOperations
};